import { Suspense, lazy } from "react";
import { Route, Navigate } from "react-router-dom";

import Loader from "./components/Loader";
import Layout from "./layout/Layout";
import { api } from "./utils/api";
import { permissionService } from "./services/permissionService";
import { renderExecutiveAnalyticsRoutes } from "../admin-shared/analytics/renderAnalyticsRoutes";

const LoginSign = lazy(() => import("./pages/LoginSign"));
const SuperAdminDashboard = lazy(() => import("./pages/SuperAdminDashboard"));
const HqAdmins = lazy(() => import("./pages/HqAdmins"));
const Regions = lazy(() => import("./pages/Regions"));
const CompanyProfile = lazy(() => import("./pages/CompanyProfile"));
const NaturalsKpis = lazy(() => import("./pages/EnterpriseKpis"));
const Complaints = lazy(() => import("./pages/Complaints"));
const HqCustomersPage = lazy(() => import("../admin-shared/HqCustomersPage"));
const HqCustomerDetailPage = lazy(() => import("../admin-shared/HqCustomerDetailPage"));
const HqCheckinAnalyticsPage = lazy(() => import("../admin-shared/HqCheckinAnalyticsPage"));
const EditProfile = lazy(() => import("./pages/EditProfile"));
const NaturalsOverviewPage = lazy(() => import("../admin-shared/pages/EnterpriseOverviewPage"));
const OverviewSectionHubPage = lazy(() => import("../admin-shared/pages/OverviewSectionHubPage"));
const LicenseManagementPage = lazy(() => import("../admin-shared/LicenseManagementPage"));
const Invoices = lazy(() => import("./pages/Invoices"));
const InvoiceDetail = lazy(() => import("./pages/InvoiceDetail"));

const RouteFallback = () => <Loader fullScreen />;
const withSuspense = (node) => <Suspense fallback={<RouteFallback />}>{node}</Suspense>;

const PermRoute = ({ children, check }) => {
  if (typeof check === "function" && !check()) {
    return <Navigate to="/nyafaz-ep/executive/overview" replace />;
  }
  return children;
};

export function renderExecutiveRoutes({ executiveHome = false } = {}) {
  return (
    <>
      {executiveHome ? <Route path="/" element={<Navigate to="/nyafaz-ep/executive" replace />} /> : null}
      <Route path="/nyafaz-ep/executive" element={<Navigate to="/nyafaz-ep/login" replace />} />
      <Route
        path="/nyafaz-ep/executive/overview"
        element={
          <Layout>
            {withSuspense(<NaturalsOverviewPage portal="executive" permissionService={permissionService} />)}
          </Layout>
        }
      />
      <Route
        path="/nyafaz-ep/executive/overview/:sectionId"
        element={
          <Layout>
            {withSuspense(
              <OverviewSectionHubPage portal="executive" permissionService={permissionService} />,
            )}
          </Layout>
        }
      />
      <Route path="/nyafaz-ep/executive/nyafaz-hub" element={<Navigate to="/nyafaz-ep/executive/overview" replace />} />
      <Route
        path="/nyafaz-ep/executive/super-admin-dashboard"
        element={<Layout>{withSuspense(<SuperAdminDashboard />)}</Layout>}
      />
      <Route
        path="/nyafaz-ep/executive/licenses"
        element={
          <Layout>
            {withSuspense(
              <LicenseManagementPage
                api={api}
                portalBase="/nyafaz-ep/executive"
                portalLabel="Executive"
                showBillingPlans={false}
                showPurchasedPlans
              />,
            )}
          </Layout>
        }
      />
      <Route path="/nyafaz-ep/executive/plans" element={<Navigate to="/nyafaz-ep/executive/overview" replace />} />
      <Route path="/nyafaz-ep/executive/hq-admins" element={<Layout>{withSuspense(<HqAdmins />)}</Layout>} />
      <Route path="/nyafaz-ep/executive/regions" element={<Layout>{withSuspense(<Regions />)}</Layout>} />
      <Route
        path="/nyafaz-ep/executive/company-profile"
        element={<Layout>{withSuspense(<CompanyProfile />)}</Layout>}
      />
      <Route
        path="/nyafaz-ep/executive/naturals-kpis"
        element={<Layout>{withSuspense(<NaturalsKpis />)}</Layout>}
      />
      <Route
        path="/nyafaz-ep/executive/store-location-requests"
        element={<Navigate to="/nyafaz-ep/executive/super-admin-dashboard" replace />}
      />
      <Route path="/nyafaz-ep/executive/complaints" element={<Layout>{withSuspense(<Complaints />)}</Layout>} />
      <Route
        path="/nyafaz-ep/executive/customers"
        element={
          <Layout>
            {withSuspense(<HqCustomersPage api={api} portalBase="/nyafaz-ep/executive" portalLabel="Executive" />)}
          </Layout>
        }
      />
      <Route
        path="/nyafaz-ep/executive/customers/:faceUserId"
        element={
          <Layout>{withSuspense(<HqCustomerDetailPage api={api} portalBase="/nyafaz-ep/executive" />)}</Layout>
        }
      />
      <Route
        path="/nyafaz-ep/executive/check-in-analytics"
        element={
          <Layout>
            {withSuspense(
              <HqCheckinAnalyticsPage
                api={api}
                portalBase="/nyafaz-ep/executive"
                portalLabel="Executive"
                Loader={Loader}
              />,
            )}
          </Layout>
        }
      />
      <Route path="/nyafaz-ep/executive/edit-profile" element={<Layout>{withSuspense(<EditProfile />)}</Layout>} />
      <Route path="/nyafaz-ep/executive/invoices" element={<Layout>{withSuspense(<Invoices />)}</Layout>} />
      <Route
        path="/nyafaz-ep/executive/invoice/:id"
        element={<Layout>{withSuspense(<InvoiceDetail />)}</Layout>}
      />
      {renderExecutiveAnalyticsRoutes({
        withSuspense,
        Layout,
        PermRoute,
        canViewSkin: () => permissionService.canAccessSkinAnalytics(),
        canViewHair: () => permissionService.canAccessHairAnalytics(),
        canViewFlow: () => permissionService.canAccessFlowAnalytics(),
        canViewGuestOps: () => permissionService.canAccessGuestOpsAnalytics(),
        hqApi: api,
      })}
    </>
  );
}
